import { Controller } from "@hotwired/stimulus";

/**
 * Stimulus controller for managing bulk actions on question templates.
 * Handles form action/method configuration based on selected bulk action.
 *
 * @class QuestionTemplateBulkController
 * @extends Controller
 */
export default class extends Controller {
  static targets = ["form", "actionSelect", "questionStart", "methodInput"];
  static values = { surveyId: String, nextPosition: String };

  /**
   * Handles bulk action selection change.
   * Updates form action, method, and additional fields based on selected action.
   * @param {Event} event
   */
  handleActionChange(event) {
    const action = this.actionSelectTarget.value;
    const surveyId = this.surveyIdValue;
    const nextPosition = this.nextPositionValue;

    switch (action) {
      case "add-to-template":
        this.configureAddToTemplate(surveyId, nextPosition);
        break;
      case "destroy-multiple":
        this.configureDestroyMultiple(surveyId);
        break;
      default:
        // No action
        break;
    }
  }

  /**
   * Configures form for "add to template" action.
   * @param {string} surveyId - Survey ID
   * @param {string} nextPosition - Next available position
   */
  configureAddToTemplate(surveyId, nextPosition) {
    // Update form action
    this.formTarget.action = `/surveys/${surveyId}/question_templates/add_multiple_to_survey`;

    // Update method to POST
    if (this.hasMethodInputTarget) {
      this.methodInputTarget.value = "post";
    }

    // Render position input using safe DOM methods
    this.renderPositionInput(nextPosition);
  }

  /**
   * Configures form for "destroy multiple" action.
   * @param {string} surveyId - Survey ID
   */
  configureDestroyMultiple(surveyId) {
    // Update form action
    this.formTarget.action = `/surveys/${surveyId}/question_templates/destroy_multiple`;

    // Update method to DELETE
    if (this.hasMethodInputTarget) {
      this.methodInputTarget.value = "delete";
    }

    // Clear additional fields and add hidden method input
    this.renderDeleteMethod();
  }

  /**
   * Renders position input field using safe DOM methods.
   * @param {string} defaultValue - Default position value
   */
  renderPositionInput(defaultValue) {
    if (!this.hasQuestionStartTarget) return;

    const container = this.questionStartTarget;

    // Clear existing content
    while (container.firstChild) {
      container.removeChild(container.firstChild);
    }

    // Create label
    const label = document.createElement("label");
    label.setAttribute("for", "at_position");
    label.textContent = "Insert At Position";

    // Create input
    const input = document.createElement("input");
    input.name = "at_position";
    input.type = "number";
    input.value = defaultValue;
    input.className = "form-control";

    container.appendChild(label);
    container.appendChild(input);
  }

  /**
   * Renders hidden method input for DELETE using safe DOM methods.
   */
  renderDeleteMethod() {
    if (!this.hasQuestionStartTarget) return;

    const container = this.questionStartTarget;

    // Clear existing content
    while (container.firstChild) {
      container.removeChild(container.firstChild);
    }

    // Create hidden method input
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = "_method";
    input.value = "delete";

    container.appendChild(input);
  }

  /**
   * Submits the bulk action form.
   * @param {Event} event
   */
  submit(event) {
    event.preventDefault();
    this.formTarget.submit();
  }
}
